<template>
    <!-- Barra fixa de decisão: só existe com caso aberto. Depois de
         registrar vira a faixa verde de confirmação. -->
    <div
        v-if="decided"
        class="border-t border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-950"
        :class="inline ? '' : 'fixed inset-x-0 bottom-0 z-40 md:left-56'"
    >
        <div
            class="flex flex-wrap items-center gap-2.5 py-3"
            :class="inline ? 'px-5' : 'px-6'"
        >
            <span class="h-2 w-2 rounded-full bg-emerald-500"></span>
            <span
                class="text-sm font-semibold text-emerald-700 dark:text-emerald-300"
            >
                {{ decidedMessage }}
            </span>
            <span class="text-xs text-emerald-700/70 dark:text-emerald-400/70">
                Caso encerrado · registrado no histórico do seller.
            </span>
            <button
                type="button"
                class="ml-auto rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs text-emerald-700 transition-colors hover:bg-emerald-100 dark:border-emerald-500/30 dark:bg-transparent dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                @click="$emit('dismiss')"
            >
                Ok, entendi
            </button>
        </div>
    </div>

    <div
        v-else-if="checkpoint"
        class="border-t border-gray-200 bg-white transition-shadow duration-500 dark:border-gray-700 dark:bg-gray-900"
        :class="[
            inline
                ? ''
                : 'fixed inset-x-0 bottom-0 z-40 shadow-[0_-8px_24px_rgba(20,21,26,0.06)] md:left-56',
            { 'ring-3 ring-inset ring-indigo-500/50': flash },
        ]"
    >
        <form
            class="flex flex-wrap items-center gap-3 py-3"
            :class="inline ? 'px-5' : 'px-6'"
            @submit.prevent="submit"
        >
            <span
                class="flex shrink-0 flex-col gap-0.5 border-r border-gray-100 pr-4 dark:border-gray-700"
            >
                <span
                    class="font-mono text-[10px] uppercase tracking-[0.14em] text-rose-600 dark:text-rose-400"
                >
                    Caso aberto · decisão
                </span>
                <span
                    class="text-xs text-gray-500 dark:text-gray-400"
                    :class="inline ? 'max-w-[150px] truncate' : ''"
                    :title="hint"
                >
                    {{ hint }}
                </span>
            </span>

            <button
                v-for="option in options"
                :key="option.value"
                type="button"
                class="flex items-center whitespace-nowrap rounded-lg border transition-colors"
                :class="[optionClass(option.value), control]"
                @click="form.decision = option.value"
            >
                {{ option.label }}
                <span
                    v-if="option.value === recommended"
                    class="ml-2 font-mono text-[9px] uppercase tracking-widest opacity-70"
                >
                    recomendado
                </span>
            </button>

            <select
                v-if="form.decision === 'estender'"
                v-model.number="form.extend_days"
                :class="control"
                class="rounded-lg border border-gray-200 bg-white px-2 text-gray-900 focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            >
                <option v-for="days in extendOptions" :key="days" :value="days">
                    +{{ days }} dias
                </option>
            </select>

            <input
                v-model="form.note"
                type="text"
                maxlength="500"
                placeholder="Justificativa — fica no histórico do seller"
                :class="control"
                class="min-w-[120px] flex-1 rounded-lg border border-gray-200 bg-white text-gray-900 focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
            />

            <button
                type="submit"
                :disabled="!canSubmit"
                class="whitespace-nowrap rounded-lg font-medium transition-colors"
                :class="[
                    control,
                    canSubmit
                        ? 'bg-indigo-800 text-white hover:bg-indigo-700'
                        : 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500',
                ]"
            >
                {{ submitting ? "Registrando..." : "Registrar decisão" }}
            </button>

            <p
                v-if="error"
                class="w-full text-xs text-rose-500 dark:text-rose-400"
            >
                {{ error }}
            </p>
        </form>
    </div>
</template>

<script>
const DECIDED_MESSAGES = {
    liberar: "Liberação registrada — a agenda segue normalmente.",
    estender: "Extensão da reserva registrada.",
    travar_saque: "Trava de saque registrada.",
};

export default {
    name: "RiskDecisionBar",

    props: {
        // Checkpoint pendente que governa o caso (null fecha a barra)
        checkpoint: { type: Object, default: null },
        // Opções de extensão vindas do backend (mesma fonte da validação)
        extendOptions: { type: Array, default: () => [15, 30, 45, 60] },
        // Flash de destaque disparado pelo tour/agente
        flash: { type: Boolean, default: false },
        // Embutida no rodapé do painel (sem position fixed)
        inline: { type: Boolean, default: false },
    },

    emits: ["decided", "dismiss"],

    data() {
        return {
            form: { decision: null, extend_days: 30, note: "" },
            submitting: false,
            error: null,
            decided: false,
            decidedMessage: "",
        };
    },

    computed: {
        options() {
            return [
                { value: "travar_saque", label: "Travar saque" },
                { value: "estender", label: "Estender reserva" },
                { value: "liberar", label: "Liberar" },
            ];
        },

        /** Tamanho dos controles: compacto quando embutida no painel. */
        control() {
            return this.inline
                ? "px-3 py-1.5 text-[13px]"
                : "px-3.5 py-2.5 text-sm";
        },

        recommended() {
            return this.checkpoint?.recommendation?.suggested ?? null;
        },

        hint() {
            if (!this.form.decision) {
                return "Escolha uma ação para registrar.";
            }

            // Mesmo mínimo do DecideReserveCheckpointRequest (note min:10):
            // evita habilitar o botão para levar 422 do backend.
            return this.form.note.trim().length < 10
                ? "Justificativa com pelo menos 10 caracteres."
                : "Pronto para registrar.";
        },

        canSubmit() {
            return (
                !this.submitting &&
                this.form.decision !== null &&
                this.form.note.trim().length >= 10
            );
        },
    },

    watch: {
        checkpoint(value, previous) {
            // Um novo caso pendente substitui a faixa de confirmação; a
            // saída do checkpoint (decidido aqui mesmo) mantém a faixa.
            if (value && value.id !== previous?.id) {
                this.form = { decision: null, extend_days: 30, note: "" };
                this.error = null;
                this.decided = false;
            }
        },
    },

    methods: {
        optionClass(value) {
            const selected = this.form.decision === value;

            if (selected && value === this.recommended) {
                return "border-indigo-600 bg-indigo-600 text-white";
            }

            if (selected) {
                return "border-indigo-800 bg-indigo-50 text-indigo-800 dark:border-indigo-400 dark:bg-indigo-500/15 dark:text-indigo-300";
            }

            return "border-gray-200 bg-white text-gray-900 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-gray-500";
        },

        submit() {
            if (!this.canSubmit) {
                return;
            }

            this.submitting = true;
            this.error = null;

            const payload = {
                decision: this.form.decision,
                note: this.form.note.trim(),
            };

            if (payload.decision === "estender") {
                payload.extend_days = this.form.extend_days;
            }

            axios
                .post(
                    `/api/financial/reserve-checkpoints/${this.checkpoint.id}/decision`,
                    payload,
                )
                .then(() => {
                    this.decided = true;
                    this.decidedMessage =
                        DECIDED_MESSAGES[payload.decision] ??
                        "Decisão registrada.";
                    this.$emit("decided", payload.decision);
                })
                .catch((error) => {
                    this.error =
                        error.response?.data?.message ??
                        "Não foi possível registrar a decisão.";
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
    },
};
</script>
